import { useQuery } from '@tanstack/react-query';
import { Skeleton, Badge, EmptyState } from '@bbpos/ui';
import { dashboardService } from '@/services/dashboard.service';
import { formatPaise, formatPaiseCompact, formatNumber } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import {
  growthBadge,
  peakHour,
  reconciledTodayPaise,
  formatHour,
} from './performance-metrics';

const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

function StatTile({ label, value, hint }: { label: string; value: string; hint?: React.ReactNode }) {
  return (
    <div className={`${GLASS_PANEL} p-5`}>
      <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        {label}
      </p>
      <p className="mt-2 text-2xl font-semibold tabular-nums text-neutral-900 dark:text-neutral-50">
        {value}
      </p>
      {hint ? <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{hint}</div> : null}
    </div>
  );
}

interface LocationPerformanceTabProps {
  locationId: string;
}

export function LocationPerformanceTab({ locationId }: LocationPerformanceTabProps) {
  const currency = useTenantCurrency();
  const summaryQuery = useQuery({
    queryKey: ['location', locationId, 'performance', 'summary'],
    queryFn: () => dashboardService.getSummary(locationId),
  });

  const waveQuery = useQuery({
    queryKey: ['location', locationId, 'performance', 'wave'],
    queryFn: () => dashboardService.getSalesWave({ locationId }),
  });

  const topProductsQuery = useQuery({
    queryKey: ['location', locationId, 'performance', 'top-products'],
    queryFn: () => dashboardService.getTopProducts(locationId, 5),
  });

  const isLoading = summaryQuery.isLoading || waveQuery.isLoading;

  if (isLoading) {
    return (
      <div className="space-y-4 animate-slide-up" role="status" aria-label="Loading performance metrics">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={96} />
          ))}
        </div>
        <Skeleton variant="rect" height={220} />
      </div>
    );
  }

  const summary = summaryQuery.data;
  const wave = waveQuery.data;
  const topProducts = topProductsQuery.data?.items ?? [];

  const todaySeries = wave?.today ?? [];
  const todayTotalPaise = reconciledTodayPaise(todaySeries);
  const peak = peakHour(todaySeries) ?? wave?.peak_hour_today ?? null;
  const maxRevenue = todaySeries.reduce((m, r) => (r.revenue > m ? r.revenue : m), 0);

  const growth = growthBadge(summary?.growth_pct);

  if (!summary || todayTotalPaise === 0) {
    return (
      <div className="animate-slide-up">
        <EmptyState
          variant="page"
          title="No sales yet today"
          description="Performance metrics will populate as bills are completed at this location."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      {/* KPI tiles — Today's row reconciles with the header Today's Sales card. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile
          label="Today's Sales"
          value={formatPaiseCompact(todayTotalPaise, currency)}
          hint={
            <span>
              {formatPaise(todayTotalPaise, currency)}
              {growth.label !== '—' ? (
                <span className="ml-2">
                  <Badge size="sm" variant={growth.tone === 'success' ? 'success' : growth.tone === 'danger' ? 'danger' : 'info'}>
                    {growth.label} vs yesterday
                  </Badge>
                </span>
              ) : null}
            </span>
          }
        />
        <StatTile
          label="Bills Today"
          value={formatNumber(summary.today.bill_count)}
          hint={`${formatNumber(summary.yesterday.bill_count)} yesterday`}
        />
        <StatTile
          label="Avg Bill Value"
          value={formatPaise(summary.today.avg_bill_value, currency)}
          hint={`${formatPaise(summary.yesterday.avg_bill_value, currency)} yesterday`}
        />
        <StatTile
          label="Peak Hour"
          value={formatHour(peak)}
          hint={maxRevenue > 0 ? `${formatPaise(maxRevenue, currency)} in that hour` : '—'}
        />
      </div>

      {/* Hourly sales bars — visualisation of `today` series from /dashboard/sales-wave. */}
      <div className={`${GLASS_PANEL} p-5`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Sales by hour (today)</h3>
          <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
            {formatPaise(todayTotalPaise, currency)} total
          </span>
        </div>
        <div className="flex items-end gap-1 h-32">
          {todaySeries.map((row) => {
            const pct = maxRevenue > 0 ? Math.max(2, Math.round((row.revenue / maxRevenue) * 100)) : 0;
            return (
              <div key={row.hour} className="flex-1 flex flex-col items-center gap-1" title={`${formatHour(row.hour)} · ${formatPaise(row.revenue, currency)} · ${row.bill_count} bills`}>
                <div
                  className="w-full rounded-t bg-gradient-to-t from-brand-500 to-indigo-500"
                  style={{ height: `${pct}%` }}
                />
                <span className="text-[10px] tabular-nums text-neutral-400">{row.hour}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top products — sourced from /bills/reports/top-products scoped by location. */}
      <div className={`${GLASS_PANEL} p-5`}>
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">Top products today</h3>
        {topProductsQuery.isLoading ? (
          <Skeleton variant="rect" height={120} />
        ) : topProducts.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No product sales recorded today.</p>
        ) : (
          <ol className="space-y-2">
            {topProducts.map((p) => (
              <li
                key={p.product_id}
                className="flex items-center justify-between rounded-lg border border-neutral-100 dark:border-neutral-700 px-3 py-2"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 text-xs font-semibold tabular-nums">
                    {p.rank}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{p.product_name}</p>
                    {p.category ? (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{p.category}</p>
                    ) : null}
                  </div>
                </div>
                <div className="text-right tabular-nums">
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{formatPaise(p.revenue, currency)}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">{formatNumber(p.quantity_sold)} sold</p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
